/**
 * @description      : App Entry Point
 * @author           : Robert Maiale
 * @created          : 01/08/2021 - 17:52:59
 * 
 * MODIFICATION LOG
 * - Version         : 1.1.2
 * - Date            : 10/05/2025
 * - Author          : Robert Maiale
 * - Modification    : Ensures validateToken runs on startup; keeps user session persistent
 **/

import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { CookiesProvider } from "react-cookie";
import { useDispatch, useSelector } from "react-redux";
import { validateToken, selectInitialized } from "./store/authSlice";

// Components
import Dashboard from "./protected/Dashboard";
import Home from "./components/Home";
import About from "./components/About";
import Register from "./components/Register";
import Contact from "./components/Contact";
import Collages from "./components/Collages";
import Upload from "./components/Upload";
import PuppyCam from "./components/PuppyCam";
import ViewItemsForSale from "./components/ViewItemsForSale";
import UploadItemForSale from "./components/UploadItemForSale";
import CollagesVideos from "./components/CollagesVideos";
import UploadVideo from "./components/UploadVideo";
import Login from "./components/Login.js";
import ProtectedRoute from "./components/ProtectedRoute";
import ManagePets from "./components/ManagePets";
// Redux
import { Provider } from "react-redux";
import store from "./store/store";

// Misc
import "./page-fade.css";
import reportWebVitals from "./reportWebVitals";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { CircularProgress, Box, Typography } from "@mui/material";

// --- 🔒 Run validation before React mounts (ensures persistent session) ---
store.dispatch(validateToken());

// ------------------------
// Auth Initializer Wrapper
// ------------------------
const AppInitializer = ({ children }) => {
  const dispatch = useDispatch();
  const initialized = useSelector(selectInitialized);

  // Optional re-validation in case of store reset
  useEffect(() => {
    if (!initialized) dispatch(validateToken());
  }, [dispatch, initialized]);

  if (!initialized) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #0D1117, #1B263B)",
          color: "#fff",
        }}
      >
        <CircularProgress sx={{ color: "#4FC3F7", mb: 2 }} />
        <Typography variant="h6">Loading Knight’s Kennel...</Typography>
      </Box>
    );
  }

  return children;
};

// ------------------------
// Main App Component
// ------------------------
const App = () => (
  <CookiesProvider>
    <Provider store={store}>
      <AppInitializer>
        <Router>
          <TransitionGroup>
            <CSSTransition timeout={300} classNames="page-fade">
              <div>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/email" element={<Contact />} />
                  <Route path="/collages" element={<Collages />} />
                  <Route path="/puppycam" element={<PuppyCam />} />
                  <Route path="/viewvideos" element={<CollagesVideos />} />
                  <Route path="/viewitemsforsale" element={<ViewItemsForSale />} />

                  {/* Protected Routes */}
                  <Route
                    path="/upload"
                    element={
                      <ProtectedRoute>
                        <Upload />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/uploadvideo"
                    element={
                      <ProtectedRoute>
                        <UploadVideo />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/uploaditemforsale"
                    element={
                      <ProtectedRoute>
                        <UploadItemForSale />
                      </ProtectedRoute>
                    }
                  />
                   <Route
                    path="/managepets"
                    element={
                      <ProtectedRoute>
                        <ManagePets />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </div>
            </CSSTransition>
          </TransitionGroup>
        </Router>
      </AppInitializer>
    </Provider>
  </CookiesProvider>
);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

serviceWorkerRegistration.register();
reportWebVitals(console.log);
